import { existsSync, mkdirSync, readFileSync, readdirSync, renameSync, statSync, writeFileSync } from "node:fs";
import { join, relative } from "node:path";
import { createHash } from "node:crypto";
import { courseNativeFingerprint, languageProfileCopyEntries, validateCourseLocaleStrings, extractCourseCopy, restoreEnglishCourseCopy } from "../features/course/copy-contract.js";
import { prepareScriptDisplayCopy } from "../features/course/script/localization.js";
import { sharedScriptLocale, sharedScriptLocaleFile } from "../features/course/script/shared-copy.js";
import { getDataJsonRoot } from "./paths.js";
import { UI_LANGS } from "../../locale-registry.mjs";

const cache = new Map();
const hash = value => createHash("sha256").update(JSON.stringify(value)).digest("hex");
const read = file => existsSync(file) ? JSON.parse(readFileSync(file, "utf8")) : null;
const files = directory => existsSync(directory) ? readdirSync(directory).flatMap(name => {
  const file = join(directory, name);
  return statSync(file).isDirectory() ? files(file) : [file];
}) : [];

function safeCwd() {
  return typeof process !== "undefined" && typeof process.cwd === "function" ? process.cwd() : "";
}

/** Exact packaged evidence; translations never become available merely because de.json exists. */
export function inspectGermanCourseReadiness(target, { websiteRoot = safeCwd(), root = join(getDataJsonRoot(), target, "courses"), auditFile } = {}) {
  try {
    const approval = auditFile || [join(root, "locales/de.audit.json"),
      join(websiteRoot, "../content/src/course/reports/german-localization", target + "-audit.json")].find(existsSync);
    const englishFile = join(root, "locales/en.json"), germanFile = join(root, "locales/de.json");
    const profileFile = join(websiteRoot, "data/language-profiles", target + ".json");
    const cacheKey = `${root}:${approval || ""}:${profileFile}`;
    if (process.env.NODE_ENV === "production" && cache.get(cacheKey)?.result.ready) return cache.get(cacheKey).result;
    const audit = approval && read(approval);
    const reviewed = audit && ["translationComplete", "protectedContentValid", "nativePreserved", "placeholderPreserved", "audioPreserved", "sourceImmutable", "reviewed"].every(key => audit[key] === true);
    if (!reviewed) return { ready: false, reason: audit ? "pending-or-invalid-review" : "missing-reviewed-artifacts" };
    const english = read(englishFile), german = read(germanFile);
    if (!english?.strings || !german?.strings) return { ready: false, reason: "missing-reviewed-artifacts" };
    const documents = [join(root, "crs001.json"), ...["scenes", "units"].flatMap(kind => files(join(root, kind)).filter(file => file.endsWith("/item.json"))), join(root, "module0.json")].filter(existsSync);
    const dependencies = [...documents, englishFile, germanFile, approval, profileFile].filter(file => file && existsSync(file));
    const revision = dependencies.map(file => { const stat = statSync(file); return `${file}:${stat.mtimeMs}:${stat.size}`; }).join("|");
    if (cache.get(cacheKey)?.revision === revision) return cache.get(cacheKey).result;
    const fail = reason => ({ ready: false, reason });
    let result;
    if (!audit || !english?.strings || !german?.strings || !existsSync(join(root, "crs001.json"))) result = fail("missing-reviewed-artifacts");
    else if ((audit.target || audit.language) !== target || audit.uiLang !== "de"
      || !["translationComplete", "protectedContentValid", "nativePreserved", "placeholderPreserved", "audioPreserved", "sourceImmutable", "reviewed"].every(key => audit[key] === true)
      || audit.semanticReview?.status !== "reviewed" || !(audit.semanticReview.reviewer || audit.reviewer)
      || audit.missing > 0 || audit.invalid?.length) result = fail("pending-or-invalid-review");
    else if (audit.englishCatalogFingerprint !== hash(english.strings) || audit.localeFingerprint !== hash(german.strings)) result = fail("stale-catalog-review");
    else {
      const records = documents.map(file => ({ file: relative(websiteRoot, file), data: read(file) }));
      const required = new Set(), audio = [];
      const visit = (value, trail, document) => {
        if (typeof value === "string") {
          if (value.startsWith("@course-copy:")) required.add(value.slice(13));
          if (["audio", "voice", "key", "say"].includes(trail.at(-1)) || trail.includes("performance")) audio.push([document, trail.join("/"), value]);
        } else if (Array.isArray(value)) value.forEach((item, index) => visit(item, [...trail, String(index)], document));
        else if (value && typeof value === "object") for (const [key, item] of Object.entries(value)) visit(item, [...trail, key], document);
      };
      records.forEach(record => visit(record.data, [], record.file));
      visit(english.lookups || {}, ["lookups"], relative(websiteRoot, englishFile));
      for (const entry of languageProfileCopyEntries(read(profileFile), english.englishOverrides || {}, english)) required.add(entry.key);
      const keys = [...required].sort();
      const coverage = validateCourseLocaleStrings(english, german, keys);
      if (!coverage.translationComplete || !coverage.protectedContentValid || hash(keys) !== hash([...(audit.requiredKeys || [])].sort())) result = fail("incomplete-live-coverage");
      else if (audit.nativeFingerprint !== courseNativeFingerprint(records.map(record => record.data), english.strings)
        || audit.audioFingerprint !== hash(audio)) result = fail("changed-native-or-audio");
      else result = { ready: true, reason: "reviewed-current-catalog", requiredKeys: keys.length };
    }
    cache.set(cacheKey, { revision, result });
    return result;
  } catch {
    return { ready: false, reason: "invalid-artifact" };
  }
}

export const courseGermanInterfaceReady = target => inspectGermanCourseReadiness(target).ready;

const localeCompleteCache = new Map();

/**
 * Validating every course interface takes about a minute and a half, and every build process (the
 * sitemap, each prerender and course-page worker, the test processes) needs the answers. They are kept
 * on disk per course revision (the same file stamps as the in-memory cache), so one process checks and
 * the others reuse; a repackaged course is checked again. LL_READINESS_CACHE=0 turns it off.
 */
const diskCacheFile = () => join(safeCwd(), ".cache/course-interface-readiness.json");
let diskCache;
function diskCached(key, revision) {
  if (process.env.LL_READINESS_CACHE === "0") return undefined;
  if (diskCache === undefined) {
    try { diskCache = JSON.parse(readFileSync(diskCacheFile(), "utf8")); } catch { diskCache = {}; }
  }
  const hit = diskCache[key];
  return hit?.revision === revision ? hit.complete : undefined;
}
function rememberOnDisk(key, revision, complete) {
  if (process.env.LL_READINESS_CACHE === "0") return;
  const file = diskCacheFile();
  try {
    // Merge with what other processes wrote meanwhile; a lost race only costs a recheck.
    let current = {};
    try { current = JSON.parse(readFileSync(file, "utf8")); } catch { /* first entry */ }
    diskCache = { ...current, ...diskCache, [key]: { revision, complete } };
    mkdirSync(join(file, ".."), { recursive: true });
    const temp = `${file}.${process.pid}.tmp`;
    writeFileSync(temp, JSON.stringify(diskCache));
    renameSync(temp, file);
  } catch { /* the cache is an optimisation only */ }
}

/** Main-course and script copy are published independently; Module 0 must not block the A1 course. */
export function courseLocaleFileComplete(target, uiLang = "de", { section = "main", root = join(getDataJsonRoot(), String(target || "").trim().toLowerCase(), "courses"), profileFile = join(safeCwd(), "data/language-profiles", String(target || "").trim().toLowerCase() + ".json") } = {}) {
  const ui = String(uiLang || "en").trim().toLowerCase();
  if (ui === "en") return true;
  const extendedInterface = ["de", "en", "it", "lt", "tr"].includes(String(target || "").trim().toLowerCase()) && section === "main" && UI_LANGS.includes(ui);
  if (!target || (ui !== "de" && !extendedInterface)) return false;
  const englishFile = join(root, "locales/en.json"), localeFile = join(root, `locales/${ui}.json`);
  const entryFile = join(root, section === "script" ? "module0.json" : "crs001.json");
  if (![englishFile, localeFile, entryFile].every(existsSync)) return false;
  const cacheKey = `${root}:${ui}:${section}:${profileFile}`;
  const cached = localeCompleteCache.get(cacheKey);
  // Packaged production snapshots are immutable, as in course bundle loading.
  // Failed checks must remain retryable while artifacts are being prepared, unless the build froze them
  // (LL_FROZEN_SNAPSHOTS: the prerender and course pages run after every snapshot is written).
  if (process.env.NODE_ENV === "production" && cached?.complete) return true;
  if (process.env.LL_FROZEN_SNAPSHOTS === "1" && cached) return cached.complete;
  const stamp = paths => paths.map(file => { const stat = statSync(file); return `${file}:${stat.mtimeMs}:${stat.size}`; }).join("|");
  const profileFiles = section === "main" && existsSync(profileFile) ? [profileFile] : [];
  const documents = section === "script" ? [entryFile] : [entryFile, ...["scenes", "units"].flatMap(kind => files(join(root, kind)).filter(file => file.endsWith("/item.json")))];
  const scriptFile = join(root, "module0.json");
  const scriptFiles = section === "main" && existsSync(scriptFile) ? [scriptFile] : [];
  const sharedFiles = section === "script" ? ["en", ui].map(sharedScriptLocaleFile).filter(existsSync) : [];
  const revision = stamp([...documents, englishFile, localeFile, ...profileFiles, ...scriptFiles, ...sharedFiles]);
  if (cached?.revision === revision) return cached.complete;
  const known = diskCached(cacheKey, revision);
  if (known !== undefined) {
    localeCompleteCache.set(cacheKey, { revision, complete: known });
    return known;
  }
  const english = read(englishFile), translated = read(localeFile);
  // An incomplete answer is kept for this revision too, so the catalogues are not parsed again.
  const incomplete = () => { localeCompleteCache.set(cacheKey, { revision, complete: false }); rememberOnDisk(cacheKey, revision, false); return false; };
  if (!english?.strings || !translated?.strings) return incomplete();
  if (ui !== "de" && translated.review?.status !== "reviewed") return incomplete();
  const required = new Set();
  const visit = value => {
    if (typeof value === "string" && value.startsWith("@course-copy:")) required.add(value.slice(13));
    else if (value && typeof value === "object") Object.values(value).forEach(visit);
  };
  let validationCatalog = english;
  if (section === "script") {
    const strings = {}, protectedFragments = { ...english.protectedFragments };
    documents.forEach(file => visit(extractCourseCopy(prepareScriptDisplayCopy(restoreEnglishCourseCopy(read(file), english.strings)), strings, ["script"], { englishOverrides: english.englishOverrides || {}, protectedFragments })));
    validationCatalog = { ...english, strings: { ...english.strings, ...strings }, protectedFragments };
  } else documents.forEach(file => visit(read(file)));
  if (section === "main") {
    visit(english.lookups || {});
    for (const entry of languageProfileCopyEntries(read(profileFile), english.englishOverrides || {}, english)) required.add(entry.key);
  }
  if (section === "main") {
    const mainKeys = new Set(required);
    required.clear();
    scriptFiles.forEach(file => visit(read(file)));
    const scriptKeys = new Set(required);
    required.clear();
    // Preserve the existing catalog-wide gate, excluding only keys used solely
    // by the script module. Shared copy and unrelated missing strings still block.
    for (const key of Object.keys(english.strings || {})) if (!scriptKeys.has(key) || mainKeys.has(key)) required.add(key);
    for (const key of mainKeys) required.add(key);
  }
  const keys = [...required];
  const overlay = section === "script" ? { strings: { ...sharedScriptLocale(ui).strings, ...translated.strings } } : translated;
  const coverage = validateCourseLocaleStrings(validationCatalog, overlay, keys);
  const complete = keys.length > 0 && keys.every(key => typeof validationCatalog.strings?.[key] === "string") && coverage.translationComplete && coverage.protectedContentValid;
  localeCompleteCache.set(cacheKey, { revision, complete });
  rememberOnDisk(cacheKey, revision, complete);
  return complete;
}

export const scriptLocaleFileComplete = (target, uiLang = "de", options = {}) => courseLocaleFileComplete(target, uiLang, { ...options, section: "script" });
